package com.advaita.cloud.drive.ui.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.SideEffect
import androidx.compose.runtime.remember
import com.advaita.cloud.drive.shared.ArchiveTree
import com.advaita.cloud.drive.shared.Delimiter
import com.advaita.cloud.drive.shared.DelimitedTextParser
import com.advaita.cloud.drive.ui.store.PreviewContent
import com.advaita.cloud.drive.ui.store.PreviewStore
import kotlinx.browser.document
import kotlinx.browser.window
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.CheckboxInput
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.Pre
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Table
import org.jetbrains.compose.web.dom.Tbody
import org.jetbrains.compose.web.dom.Td
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Th
import org.jetbrains.compose.web.dom.Thead
import org.jetbrains.compose.web.dom.Tr
import org.w3c.dom.events.Event
import org.w3c.dom.events.EventListener
import org.w3c.dom.events.MouseEvent

/**
 * The ADV-1200 viewer shell: a resizable pane anchored to the right of the file
 * table, filling the height of the app shell.
 *
 * It is a SIBLING of [FileTable] inside `.workspace`, not a child, so ADV-1199's
 * folder tree can be added to its left without touching either. Its header and
 * footer are fixed and only the body scrolls, so the filename and the size line
 * stay put while you read.
 *
 * The pane carries NO download action. Downloading is the row's job (the ⬇ icon
 * on every file row); duplicating it here put two affordances for the same act
 * on screen at once.

 *
 * Everything format-specific lives in the per-renderer composables below.
 * Adding a format is a branch in [PreviewBody] plus a policy entry on the
 * backend — no fetching, no auth, no ceiling logic.
 */
@Composable
fun PreviewPane(store: PreviewStore) {
    if (!store.isOpen) return
    Div(attrs = {
        classes("preview-pane")
        attr("data-testid", "preview-pane")
        // Width is state-driven so the splitter can move it; the stylesheet only
        // supplies the initial value.
        style { property("flex", "0 0 ${store.widthPx}px") }
    }) {
        PreviewHead(store)
        Div(attrs = {
            classes("preview-body")
            // The tabular branch supplies its own scrollport (.preview-table-wrap)
            // so its sticky header has a scrolling ancestor. Two nested scrollports
            // would leave the header pinned to a box that never scrolls.
            // The archive list is the same table in the same scrollport (ADV-1228).
            val content = store.content
            if (content is PreviewContent.TableContent || content is PreviewContent.ArchiveContent) classes("framed")
            attr("data-testid", "preview-body")
        }) {
            PreviewBody(store)
        }
        PreviewFoot(store)
    }
}

/**
 * The drag grip, rendered BETWEEN the table and the pane as its own flex item,
 * so the grab target is the gap itself rather than a strip overlapping the
 * pane's left edge.
 *
 * Placed by [com.advaita.cloud.drive.ui.App] rather than by [PreviewPane],
 * because a child cannot position itself between its own parent's siblings.
 */
@Composable
fun PreviewSplitter(store: PreviewStore) {
    if (!store.isOpen) return
    Div(attrs = {
        classes("preview-splitter")
        attr("data-testid", "preview-splitter")
        attr("aria-label", "Resize preview")
        onMouseDown { event ->
            // Without this the browser starts a text/drag selection instead.
            event.preventDefault()
            beginResize(store, event.clientX.toDouble())
        }
    }) {}
}

/**
 * Raw window listeners rather than Compose's element-scoped mouse events, on
 * purpose: a resize drag has to keep tracking once the cursor leaves the 17px
 * grip, which an element handler cannot do.
 *
 * Teardown has THREE triggers, not one. Releasing the button outside the
 * browser window delivers `mouseup` to whatever is under the cursor, not to us
 * — so a mouseup-only teardown left the pane resizing on every later mouse
 * move with no button held, `user-select: none` stuck on the body, and another
 * listener pair added on each subsequent grab. Only a reload cleared it.
 * `buttons == 0` catches the re-entry, and `blur` catches leaving the window at
 * all.
 */
private fun beginResize(store: PreviewStore, startClientX: Double) {
    val startWidth = store.widthPx
    // EventListener OBJECTS rather than Kotlin lambdas, because teardown has to
    // remove exactly what it added. removeEventListener compares JS function
    // identity, and a function-typed argument to an external declaration is not
    // guaranteed to reach JS as the same object at two call sites — so an
    // add/remove pair written with one lambda reference can still no-op and
    // leave one live listener per grab behind. An object is the same object.
    var move: EventListener? = null
    var up: EventListener? = null
    var blur: EventListener? = null

    // Three triggers can all fire for one drag; only the first may act. Without
    // the latch, persistWidth wrote to localStorage on every later mouse move.
    var finished = false
    val finish = {
        if (!finished) {
            finished = true
            window.removeEventListener("mousemove", move)
            window.removeEventListener("mouseup", up)
            window.removeEventListener("blur", blur)
            document.body?.style?.removeProperty("user-select")
            store.persistWidth()
        }
    }

    move = object : EventListener {
        override fun handleEvent(event: Event) {
            val mouse = event as MouseEvent
            // No button held: the release happened somewhere we never saw it.
            if (mouse.buttons.toInt() == 0) {
                finish()
            } else {
                // Dragging LEFT widens the pane, so the delta is start-minus-current.
                store.setWidth(startWidth + (startClientX - mouse.clientX.toDouble()).toInt())
            }
        }
    }
    up = object : EventListener {
        override fun handleEvent(event: Event) = finish()
    }
    blur = object : EventListener {
        override fun handleEvent(event: Event) = finish()
    }

    // Otherwise the whole table text-selects as the pointer sweeps across it.
    document.body?.style?.setProperty("user-select", "none")
    window.addEventListener("mousemove", move)
    window.addEventListener("mouseup", up)
    window.addEventListener("blur", blur)
}

@Composable
private fun PreviewHead(store: PreviewStore) {
    val file = store.selected
    Div(attrs = { classes("preview-head") }) {
        Span(attrs = {
            classes("preview-title")
            file?.let { attr("data-tip", it.displayName) }
        }) {
            if (file != null) {
                Icon(IconName.FILE)
                Text(" ${file.displayName}")
            } else {
                Text("Preview")
            }
        }
        Div(attrs = { classes("preview-actions") }) {
            Button(attrs = {
                classes("btn", "icon")
                attr("data-tip", "Close preview")
                attr("aria-label", "Close preview")
                attr("data-testid", "preview-close")
                onClick { store.close() }
            }) { Icon(IconName.CLOSE) }
        }
    }
}

@Composable
private fun PreviewBody(store: PreviewStore) {
    when (val content = store.content) {
        // Open with nothing selected: we navigated away from the previewed
        // file. Kept docked so the table's width does not jump while browsing.
        null -> Div(attrs = {
            classes("preview-status")
            attr("data-testid", "preview-empty")
        }) { Text("Select a file to preview it.") }

        PreviewContent.Loading ->
            Div(attrs = { classes("preview-status") }) { Text("Loading preview…") }

        is PreviewContent.TextContent -> TextPreview(content)

        is PreviewContent.TableContent -> TablePreview(store, content)

        is PreviewContent.ImageContent -> ImagePreview(store, content)

        is PreviewContent.ArchiveContent -> ArchivePreview(store, content)

        // Not an error state: "no preview for this type" is a normal answer, and
        // the row's ⬇ remains the way to get the bytes.
        is PreviewContent.Unavailable -> PreviewMessage(content.message)

        is PreviewContent.Failed -> PreviewMessage(content.message, isError = true) {
            Button(attrs = {
                classes("btn")
                attr("data-testid", "preview-retry")
                onClick { store.retry() }
            }) { Text("Retry") }
        }
    }
}

/**
 * ADV-1203. Rendered through Compose [Text] into a `<pre>`, which escapes, so
 * no preview content is ever interpreted as markup. Whitespace is preserved by
 * `<pre>` plus the `pre-wrap` rule on `.preview-text`.
 */
@Composable
private fun TextPreview(content: PreviewContent.TextContent) {
    Pre(attrs = { classes("preview-text"); attr("data-testid", "preview-text") }) {
        Text(content.text)
    }
}

/**
 * Hard bounds on the drawn grid. This is how the preview stays cheap INSTEAD of
 * virtualizing: ADV-1202 asks for virtualized rows so a large file cannot stall
 * the browser, and a bound plus an explicit notice buys the same guarantee for
 * a fraction of the complexity. The byte ceiling (25 KB) already makes these
 * rarely bite; they are the backstop for a raised ceiling or a pathological file.
 *
 * Columns are bounded too — a wide matrix does not need every column on screen
 * to be a useful peek.
 */
private const val MAX_PREVIEW_ROWS = 500
private const val MAX_PREVIEW_COLUMNS = 40

/**
 * ADV-1202. Parses on every recomposition rather than caching a grid: at a
 * 25 KB ceiling the parse is trivial, and the delimiter and header controls
 * would otherwise need a second representation kept in sync with the text.
 */
@Composable
private fun TablePreview(store: PreviewStore, content: PreviewContent.TableContent) {
    val fileName = store.selected?.displayName ?: ""
    // A DERIVED payload is comma-separated because the server built it that
    // way, so there is nothing to detect and detecting anyway is unsafe.
    // Detection scans the text once per candidate delimiter, and the quote
    // scanner only honours a quote that opens a field of THAT candidate. Under
    // `;` the comma never opens a field, so every semicolon inside a quoted
    // cell reads as structure. A column of UniProt accessions
    // (`sp|P04406|G3P_HUMAN`) or of equal-length GO lists therefore ties the
    // score on agreement and wins it on field count, and the grid comes out
    // wrong. Quoting every field on the server stops the PARSE breaking, and
    // cannot stop the detection choosing.
    val delimiter = store.delimiterOverride
        ?: if (store.descriptor?.derived == true) {
            Delimiter.COMMA
        } else {
            DelimitedTextParser.detectDelimiter(content.text, fileName)
        }
    val table = DelimitedTextParser.parse(
        text = content.text,
        delimiter = delimiter,
        firstRowIsHeader = store.firstRowIsHeader,
        // The byte cut lands mid-line, so the final record is half a row.
        dropPartialLastRecord = content.truncated,
        maxRows = MAX_PREVIEW_ROWS,
        maxColumns = MAX_PREVIEW_COLUMNS,
    )

    Div(attrs = { classes("preview-toolbar") }) {
        Label(attrs = { classes("preview-toolbar-item") }) {
            Span { Text("Delimiter") }
            Select(attrs = {
                classes("preview-delimiter")
                attr("data-testid", "preview-delimiter")
                attr("aria-label", "Delimiter")
                onChange { event ->
                    store.overrideDelimiter(
                        Delimiter.entries.firstOrNull { it.name == event.value },
                    )
                }
            }) {
                Delimiter.entries.forEach { candidate ->
                    Option(
                        value = candidate.name,
                        attrs = { if (candidate == delimiter) selected() },
                    ) { Text(candidate.label) }
                }
            }
        }
        Label(attrs = { classes("preview-toolbar-item") }) {
            CheckboxInput(checked = store.firstRowIsHeader, attrs = {
                attr("data-testid", "preview-header-toggle")
                onChange { event -> store.setFirstRowIsHeader(event.value) }
            })
            Span { Text("First row is header") }
        }
    }

    if (table.rows.isEmpty()) {
        // Reachable with the PRODUCTION header default: when the 25 KB prefix
        // holds only a header plus a sliced record, the slice is dropped and the
        // survivor becomes column names, leaving no body. Drawing headings over
        // an empty grid explained nothing, and rowsClipped is false here so the
        // notice below would not have fired either.
        val why = if (content.truncated) {
            "The first ${formatSizeLabel(store.descriptor?.previewBytes)} of this file " +
                "did not contain a complete data row."
        } else {
            "This file has no rows to show."
        }
        Div(attrs = {
            classes("preview-status")
            attr("data-testid", "preview-no-rows")
        }) { Text(why) }
        return
    }

    // The SERVER can stop before the client's own bounds are reached: a derived
    // table is clipped by a row budget and a payload budget that the browser
    // never sees. That leaves a grid the client had no reason to clip, so
    // neither `rowsClipped` nor `columnsClipped` fires, and the footer phrase is
    // suppressed for a derived payload because its byte count describes the
    // workbook rather than the table. Without this the preview reads as the
    // whole sheet.
    val serverClipped = content.truncated && store.descriptor?.derived == true
    if (table.rowsClipped || table.columnsClipped || serverClipped) {
        val clipped = buildList {
            // "N of M rows" is only honest when M is the file's row count. Once
            // the bytes were truncated, M is the row count of the 25 KB PREFIX —
            // a 40 MB csv read "500 of 512 rows" while holding ~800,000.
            if (table.rowsClipped) {
                add(
                    if (content.truncated) "the first ${table.rows.size} rows"
                    else "${table.rows.size} of ${table.totalRowCount} rows",
                )
            }
            if (table.columnsClipped) add("${table.columnCount} of ${table.totalColumnCount} columns")
        }
        // Never silent: a clipped grid that says nothing reads as the whole file.
        Div(attrs = {
            classes("preview-table-notice")
            attr("data-testid", "preview-table-notice")
        }) {
            Text(
                if (clipped.isEmpty()) {
                    "Showing part of this workbook. Download it to see the rest."
                } else {
                    "Showing ${clipped.joinToString(" and ")}"
                },
            )
        }
    }

    Div(attrs = { classes("preview-table-wrap") }) {
        Table(attrs = { classes("preview-table"); attr("data-testid", "preview-table") }) {
            table.header?.let { header ->
                Thead {
                    Tr {
                        header.forEach { cell -> Th { Text(cell) } }
                    }
                }
            }
            Tbody {
                table.rows.forEach { row ->
                    Tr {
                        row.forEach { cell -> Td { Text(cell) } }
                    }
                }
            }
        }
    }
}

/**
 * ADV-1207. The `src` is a blob URL minted by [PreviewStore], not an API path:
 * the bearer token lives in JS, so an `<img>` cannot authenticate against
 * `/api`. The store owns the URL and revokes it.
 *
 * The image is never the file's own pixels past the edge limit — it is a
 * server-side rendition — and the footer says so.
 */
@Composable
private fun ImagePreview(store: PreviewStore, content: PreviewContent.ImageContent) {
    Div(attrs = {
        classes("preview-image-wrap")
        attr("data-testid", "preview-image-wrap")
    }) {
        Img(
            src = content.objectUrl,
            alt = store.selected?.displayName ?: "Image preview",
            attrs = {
                classes("preview-image")
                attr("data-testid", "preview-image")
            },
        )
    }
}

/**
 * ADV-1228. A zip's entries as a folder you can walk. The server listed the
 * central directory without opening the archive; the folders, including the
 * ones the archive never declared, come from [com.advaita.cloud.drive.shared.ArchiveTree].
 *
 * Every name goes through Compose [Text], which escapes, so an entry called
 * `<img onerror=...>` is drawn as those characters. No name ever reaches
 * innerHTML.
 *
 * The notice is never optional when the listing was cut short: a partial
 * listing that says nothing reads as the whole archive.
 */
/**
 * One breadcrumb. A level that would only lead back to [shown] — the level itself, or a
 * single-folder level skipped on the way down — is plain text, not a link that does
 * nothing.
 */
@Composable
private fun ArchiveCrumb(
    store: PreviewStore,
    tree: ArchiveTree,
    prefix: List<String>,
    shown: List<String>,
    label: String,
) {
    if (tree.isCrumbTarget(prefix, shown)) {
        Button(attrs = {
            classes("preview-archive-crumb")
            onClick { store.openArchiveFolder(prefix) }
        }) { Text(label) }
    } else {
        Span(attrs = { classes("preview-archive-crumb-static") }) { Text(label) }
    }
}

@Composable
private fun ArchivePreview(store: PreviewStore, content: PreviewContent.ArchiveContent) {
    val listing = content.listing
    // A level holding only one folder is skipped on the way down (ArchiveTree.landing).
    // Also in a truncated listing: a root that LOOKS like one folder may hold more that
    // the listing never reached, but the truncation notice says so, Up leads back, and
    // an archive of one folder is by far the common shape.
    // Resolving the path walks the raw tree and sorts nothing. The view is keyed on the
    // RESOLVED path, so the store catching up below does not sort the level again.
    val shownPath = remember(content.tree, store.archivePath) { content.tree.landing(store.archivePath) }
    val view = remember(content.tree, shownPath) { content.tree.folderOrRoot(shownPath) }
    // The store follows the level actually shown — a path that no longer resolves, or one
    // a single-folder level led past — so Up and the crumbs match.
    SideEffect { if (shownPath != store.archivePath) store.openArchiveFolder(shownPath) }
    // Up skips the single-folder levels too, or it would land straight back here.
    val upPath = remember(content.tree, shownPath) { content.tree.upFrom(shownPath) }

    Div(attrs = {
        classes("preview-toolbar")
        attr("data-testid", "preview-archive-toolbar")
    }) {
        Button(attrs = {
            classes("btn")
            attr("data-testid", "preview-archive-up")
            attr("aria-label", "Up one folder")
            if (upPath == null) disabled()
            onClick { upPath?.let { store.openArchiveFolder(it) } }
        }) { Text("Up") }
        Span(attrs = {
            classes("preview-archive-crumbs")
            attr("data-testid", "preview-archive-crumbs")
        }) {
            ArchiveCrumb(store, content.tree, emptyList(), shownPath, store.selected?.displayName ?: "Archive")
            shownPath.forEachIndexed { index, segment ->
                Span { Text(" / ") }
                ArchiveCrumb(store, content.tree, shownPath.take(index + 1), shownPath, segment)
            }
        }
    }

    if (content.truncated) {
        Div(attrs = {
            classes("preview-table-notice")
            attr("data-testid", "preview-archive-notice")
        }) {
            Text(
                "Showing ${listing.entries.size} of ${listing.totalEntries} entries. " +
                    "The listing is truncated. Download the archive to see every entry.",
            )
        }
    }

    if (view.folders.isEmpty() && view.files.isEmpty()) {
        Div(attrs = {
            classes("preview-status")
            attr("data-testid", "preview-archive-empty")
        }) { Text(if (listing.totalEntries == 0L) "This archive is empty." else "This folder is empty.") }
        return
    }

    Div(attrs = { classes("preview-table-wrap") }) {
        Table(attrs = {
            classes("preview-table", "preview-archive-table")
            attr("data-testid", "preview-archive-table")
        }) {
            Thead {
                Tr {
                    Th { Text("Name") }
                    Th { Text("Size") }
                }
            }
            Tbody {
                view.folders.forEach { folder ->
                    Tr(attrs = { attr("data-testid", "preview-archive-folder") }) {
                        Td {
                            Button(attrs = {
                                classes("preview-archive-link")
                                onClick { store.openArchiveFolder(folder.path) }
                            }) {
                                Icon(IconName.FOLDER)
                                Text(" ${folder.name}")
                            }
                        }
                        Td { }
                    }
                }
                view.files.forEach { file ->
                    Tr(attrs = { attr("data-testid", "preview-archive-file") }) {
                        Td {
                            Icon(IconName.FILE)
                            Text(" ${file.name}")
                            if (file.encrypted) {
                                Span(attrs = {
                                    classes("preview-archive-badge")
                                    attr("data-testid", "preview-archive-locked")
                                    attr("data-tip", "Password-protected")
                                }) { Text("locked") }
                            }
                            if (file.unsafePath) {
                                Span(attrs = {
                                    classes("preview-archive-badge", "warn")
                                    attr("data-testid", "preview-archive-unsafe")
                                    attr("data-tip", "This name points outside the archive, or nests too deep for folders, so it is shown as written.")
                                }) { Text("unsafe path") }
                            }
                        }
                        Td { Text(if (file.directory) "" else formatSize(file.size)) }
                    }
                }
            }
        }
    }
}

@Composable
private fun PreviewMessage(
    message: String,
    isError: Boolean = false,
    action: @Composable () -> Unit = {},
) {
    Div(attrs = {
        classes("preview-status")
        if (isError) classes("error")
        attr("data-testid", "preview-message")
    }) {
        Div { Text(message) }
        action()
    }
}

/**
 * The footer is where truncation stops being silent — ADV-1202 and ADV-1203
 * both require the user to be told when they are not seeing the whole file.
 * Truncation is taken from the fetched content, not the descriptor, because the
 * response header is the authoritative signal.
 */
@Composable
private fun PreviewFoot(store: PreviewStore) {
    val descriptor = store.descriptor ?: return
    val content = store.content
    val truncated = when (content) {
        is PreviewContent.TextContent -> content.truncated
        is PreviewContent.TableContent -> content.truncated
        is PreviewContent.ArchiveContent -> content.truncated
        else -> false
    }
    val parts = buildList {
        add(formatSizeLabel(descriptor.sizeBytes))
        descriptor.mimeType?.let { add(it) }
        // previewBytes counts the bytes read from the FILE, which for a derived
        // payload is the workbook rather than the csv built from it. Saying
        // "showing the first 4 KB" of a 450-byte table is worse than saying
        // nothing, and the notice above the table already reports the rows.
        if (truncated && store.descriptor?.derived != true) {
            add("showing the first ${formatSizeLabel(descriptor.previewBytes)}")
        }
        // ADV-1207: an image is downscaled rather than clipped, so it needs its
        // own sentence. Absent when the rendition IS the original.
        if (content is PreviewContent.ImageContent && content.rendition != null) {
            add("reduced to ${content.rendition}")
        }
        // ADV-1228: the archive's own count, which is what the listing is of.
        // previewBytes is not shown for it (derived), for the workbook's reason.
        if (content is PreviewContent.ArchiveContent) {
            val total = content.listing.totalEntries
            add(if (total == 1L) "1 entry" else "$total entries")
        }
    }
    Div(attrs = {
        classes("preview-foot")
        if (truncated) classes("truncated")
        attr("data-testid", "preview-foot")
    }) {
        Text(parts.joinToString(" · "))
    }
}

/** Shares [formatSize] with the table so a row and the pane never disagree. */
private fun formatSizeLabel(bytes: Long?): String =
    if (bytes == null) "unknown size" else formatSize(bytes)
